import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  Stack,
  Link,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import { getPostLoginRedirectPath } from '../utils/authRedirect';
import { stashProvisionalLoginPassword } from '../utils/passwordManager';
import { SIMULATION_SETUP_PATH } from '../constants/clientRoutes';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth(); // Removed loginAsGuest - disabled

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);
    
    if (!result.success) {
      setError(result.error);
    } else if (result.user?.mustCompletePasswordOnboarding) {
      stashProvisionalLoginPassword(formData.password);
      setFormData((prev) => ({ ...prev, password: '' }));
    } else {
      const postLoginPath = getPostLoginRedirectPath(location.search, SIMULATION_SETUP_PATH);
      navigate(postLoginPath, { replace: true });
    }
    
    setLoading(false);
  };

  // Guest login function commented out - guest access disabled
  /*
  const handleGuestLogin = () => {
    loginAsGuest();
    navigate('/personas');
  };
  */

  return (
    <PageLayout showNavbar={false}>
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ py: 4 }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, sm: 4, md: 5 }, 
            width: '100%', 
            maxWidth: 450,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          {/* Logo/Brand Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/images/logo.png"
              alt="Clinical Theater Logo"
              sx={{
                width: 72,
                height: 72,
                mx: 'auto',
                mb: 2,
                borderRadius: 0.25,
                boxShadow: 3,
                objectFit: 'contain',
                backgroundColor: 'grey.100'
              }}
            />
            <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
              Clinical Theater Client Portal
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" autoComplete="off" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="username"
                autoFocus
                variant="outlined"
                sx={{
                  '& .MuiInputLabel-root': {
                    fontWeight: 500
                  }
                }}
              />
              
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="off"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontWeight: 500
                  }
                }}
              />

              <Box sx={{ textAlign: 'right' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/forgot-password')}
                  sx={{
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Forgot password?
                </Link>
              </Box>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                endIcon={loading ? null : <ArrowForwardIcon />}
                sx={{ 
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </PageLayout>
  );
};

export default Login;
